import { ReactNode } from "react"
import { classNames } from "../../utils"
import { useUrlTabSync } from "../../hooks/use-url-tab-sync"

const commonTabClassName =
  "whitespace-nowrap py-4 px-4 border-b-[3px] font-medium text-sm cursor-pointer"
const defaultTabClassName =
  "border-transparent text-neutral-400 hover:text-neutral-500 hover:border-neutral-400"
const activeTabClassName = "border-primary-400 text-gray-700"

export interface TabProps<TTabId = string> {
  id: TTabId
  label: string
  className?: string
  /** Small dot after the label: unsaved edits or something to review. */
  indicator?: "dirty" | "warning"
}

interface Props<TTabId = string> {
  tabs: TabProps<TTabId>[]
  currentTab: TTabId
  onChange: (tabId: TTabId) => void
  className?: string
  extraElement?: ReactNode
  /** Query param name to sync with. When set, clicking a tab updates the URL
   *  and browser back/fwd navigation updates the active tab. */
  urlParam?: string
  /** Use `history.replace` instead of `history.push` when updating the URL.
   *  Defaults to `true`. */
  replaceUrl?: boolean
}

const Tabs = <TTabId extends string>({
  tabs,
  currentTab,
  onChange,
  extraElement,
  className = "",
  urlParam,
  replaceUrl = true
}: Props<TTabId>) => {
  const handleChange = useUrlTabSync(
    tabs,
    urlParam,
    onChange as (id: string) => void,
    replaceUrl
  )

  return (
    <div
      className={`flex justify-between items-center border-b border-gray-200 overflow-x-auto overflow-y-hidden ${className}`}
    >
      <nav className="-mb-px flex space-x-2" aria-label="Tabs">
        {tabs.map(tab => (
          <div
            key={String(tab.id)}
            className={classNames(
              tab.id === currentTab ? activeTabClassName : defaultTabClassName,
              commonTabClassName,
              tab.className
            )}
            onClick={() => handleChange(String(tab.id))}
          >
            {tab.label}
            {tab.indicator && (
              <span
                className={classNames(
                  "ml-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle",
                  tab.indicator === "warning"
                    ? "bg-orange-400"
                    : "bg-primary-400"
                )}
                aria-label={
                  tab.indicator === "warning"
                    ? "Needs review"
                    : "Unsaved changes"
                }
              />
            )}
          </div>
        ))}
      </nav>
      {!!extraElement && <div>{extraElement}</div>}
    </div>
  )
}

export default Tabs
